import { useEffect, useRef } from "react";
import styles from "@/asserts/styles/formSection.module.less";
import ModalSectionHeader from "@/components/ui/ModalSectionHeader";
import { Form, Input, Radio, Table } from "antd";
import { checklistConfig } from "./checklist.config";
import type { ColumnType } from "antd/es/table";
import clsx from "clsx";
import type { NamePath } from "antd/es/form/interface";
import type { PurchaseContractFormValues } from "../../type";
import { useTranslation } from "react-i18next";

interface ChecklistConfigItem {
  checkPoint: string;
  companyStandard: string;
  type: string;
  name: string;
  options?: { value: number; label: string }[];
  unit?: string;
  /** 当选择值为 otherValue 时显示备注输入框 */
  showRemarkWhenOther?: boolean;
  remarkFieldName?: string;
  otherValue?: number;
}

type ChecklistItem = ChecklistConfigItem;

/**
 * 根据 materialType 和 contractSubCategory 获取对应的 checklist 配置类型
 */
function getChecklistConfigType(
  materialType?: string,
  contractSubCategory?: string,
): "red" | "purple" | "blue" {
  if (materialType === "C1") {
    return "red";
  }

  if (
    materialType === "C2" &&
    (contractSubCategory === "C2_1" || contractSubCategory === "C2_2")
  ) {
    return "purple";
  }

  return "blue";
}

/**
 * 根据 materialType 和 contractSubCategory 获取对应的 checklist 配置
 */
function getChecklistConfig(
  materialType?: string,
  contractSubCategory?: string,
): ChecklistConfigItem[] {
  const type = getChecklistConfigType(materialType, contractSubCategory);
  return checklistConfig[type];
}

/**
 * 合同审查信息
 */
export function CheckListFormFields() {
  const { t } = useTranslation("contractManagement");
  const form = Form.useFormInstance<PurchaseContractFormValues>();

  const materialType = Form.useWatch("materialType", form);
  const contractSubCategory = Form.useWatch("contractSubCategory", form);

  const currentChecklistConfig = getChecklistConfig(
    materialType,
    contractSubCategory,
  );

  const prevConfigTypeRef = useRef<string | null>(null);
  const contractReviewItems = Form.useWatch("contractReviewItems", form);

  // 当配置类型变化时，重置 contractReviewItems 表单项值
  useEffect(() => {
    const currentConfigType = getChecklistConfigType(
      materialType,
      contractSubCategory,
    );

    // 如果已经有关联的值（详情数据），则不重置
    if (contractReviewItems && Object.keys(contractReviewItems).length > 0) {
      prevConfigTypeRef.current = currentConfigType;
      return;
    }

    // 配置类型真正变化时重置
    if (prevConfigTypeRef.current !== currentConfigType) {
      form.setFieldsValue({ contractReviewItems: undefined });
      prevConfigTypeRef.current = currentConfigType;
    }
  }, [materialType, contractSubCategory, form, contractReviewItems]);

  const renderFormItem = (record: ChecklistItem) => {
    const fieldName = ["contractReviewItems", record.name] as NamePath;

    if (record.type === "radio" && record.options) {
      // 处理带有备注输入框的 radio 类型（如"保险"选项）
      if (record.showRemarkWhenOther) {
        const remarkFieldName = [
          "contractReviewItems",
          record.remarkFieldName,
        ] as NamePath;
        const otherValue = record.otherValue ?? 2;

        return (
          <Form.Item shouldUpdate noStyle>
            {() => {
              const value = form.getFieldValue(fieldName);
              const showRemark = value === otherValue;

              return (
                <div className={styles.remarkContainer}>
                  <Form.Item
                    name={fieldName}
                    rules={[
                      {
                        required: true,
                        message: t("请选择"),
                      },
                    ]}
                    noStyle
                  >
                    <Radio.Group options={record.options} />
                  </Form.Item>
                  {showRemark && (
                    <Form.Item
                      name={remarkFieldName}
                      rules={[
                        {
                          required: true,
                          message: t("请输入备注"),
                        },
                      ]}
                      noStyle
                    >
                      <Input
                        placeholder={t("请输入备注")}
                        style={{ marginTop: 8 }}
                      />
                    </Form.Item>
                  )}
                </div>
              );
            }}
          </Form.Item>
        );
      }

      return (
        <Form.Item
          name={fieldName}
          rules={[{ required: true, message: t("请选择") }]}
          noStyle
        >
          <Radio.Group options={record.options} />
        </Form.Item>
      );
    }

    if (record.type === "input") {
      return (
        <Form.Item
          name={fieldName}
          rules={[{ required: true, message: t("请输入") }]}
          noStyle
        >
          <Input
            placeholder={t("请输入")}
            suffix={record.unit}
            style={{ width: "100%" }}
          />
        </Form.Item>
      );
    }

    return null;
  };

  const columns = [
    {
      dataIndex: "checkPoint",
      key: "checkPoint",
      title: t("合同检查项"),
      width: "30%",
      align: "center",
    },
    {
      dataIndex: "companyStandard",
      key: "companyStandard",
      title: t("公司标准"),
      width: "35%",
      align: "center",
    },
    {
      dataIndex: "actual",
      key: "actual",
      title: t("实际"),
      width: "35%",
      align: "center",
      render: (_: unknown, record: ChecklistItem) => {
        // input 类型不需要自定义错误显示，直接渲染
        if (record.type === "input") {
          return renderFormItem(record);
        }

        // radio 类型需要自定义错误样式
        const fieldName = ["contractReviewItems", record.name] as NamePath;

        return (
          <Form.Item shouldUpdate noStyle>
            {() => {
              const hasError = form.getFieldError(fieldName).length > 0;

              return (
                <div
                  className={clsx({
                    [styles.errorCell]: hasError,
                  })}
                >
                  {renderFormItem(record)}
                </div>
              );
            }}
          </Form.Item>
        );
      },
    },
  ];

  return (
    <div className={styles.formSection}>
      <ModalSectionHeader title={t("合同审查项")} />

      <div className={styles.sectionBody}>
        <Table
          dataSource={currentChecklistConfig}
          columns={columns as ColumnType[]}
          rowKey="checkPoint"
          pagination={false}
          bordered
          size="small"
        />
      </div>
    </div>
  );
}
